import React, { useEffect } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { useVoyageContextV2 } from "../Contexts/VoyageContextV2";
import { useTranslation } from "react-i18next";
import { getVoyageNumber } from "../utils/domainSelectors";
import {
  getSelectedTrip,
  getVoyageTripsArray,
} from "../utils/voyageSelection";

const getTripLocationLabel = (trip, t) => {
  const operationType = trip?.operationType || t("trip");
  const from = trip?.from?.trim?.() || "";
  const to = trip?.to?.trim?.() || "";
  const fallbackPort = t("noTrips");

  if (operationType === "Loading") {
    return `${operationType}: ${to || from || fallbackPort}`;
  }

  if (operationType === "Discharging") {
    return `${operationType}: ${to || from || fallbackPort}`;
  }

  if (from && to && from !== to) {
    return `${operationType}: ${from} -> ${to}`;
  }

  return `${operationType}: ${to || from || fallbackPort}`;
};

const VoyageSelectBanner = ({ showCargoSelect = true }) => {
  const theme = useTheme();
  const { t } = useTranslation("voyageselect");

  const {
    selectedTripId,
    selectedCargoIndex,
    setSelectedTripId,
    setSelectedCargoIndex,
    currentVoyage,
  } = useVoyageContextV2();
  const darkMode = theme.palette.mode === "dark";

  // 1. Automatisk Default-hantering
  useEffect(() => {
    if (currentVoyage?.trips) {
      const trips = getVoyageTripsArray(currentVoyage);
      const tripIds = trips.map((trip) => trip.tripId).filter(Boolean);
      const selectedTrip = getSelectedTrip(currentVoyage, selectedTripId);

      // Om vi saknar ett valt tripID, eller om det ID vi har inte längre finns i nuvarande voyage
      if (tripIds.length > 0 && (!selectedTripId || !selectedTrip)) {
        // Sätt den första trippen som default (ID:t är nyckeln i objektet)
        const firstTripId = tripIds[0];
        setSelectedTripId(firstTripId);
        setSelectedCargoIndex(0);
      }
    }
  }, [
    currentVoyage,
    selectedTripId,
    setSelectedTripId,
    setSelectedCargoIndex,
  ]);

  // 2. Förbered data för rendering
  const tripsArray = getVoyageTripsArray(currentVoyage);
  const selectedTripObject = getSelectedTrip(currentVoyage, selectedTripId);

  // 3. Handlers
  const handleTripChange = (event) => {
    const newTripId = event.target.value;
    // Nollställ alltid cargo till index 0 vid tripp-byte
    setSelectedTripId(newTripId);
    setSelectedCargoIndex(0);
  };

  const handleCargoChange = (event) => {
    setSelectedCargoIndex(event.target.value);
  };

  // Synka cargo-index om komponenten döljer cargo-val
  useEffect(() => {
    if (!showCargoSelect && selectedCargoIndex !== 0) {
      setSelectedCargoIndex(0);
    }
  }, [showCargoSelect, selectedCargoIndex, setSelectedCargoIndex]);

  if (!currentVoyage) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        padding: "10px 24px",
        bgcolor: darkMode
          ? alpha(theme.palette.background.paper, 0.9)
          : "primary.dark",
        justifyContent: "center",
        color: "white",
        boxShadow: theme.shadows[2],
        backdropFilter: "blur(8px)",
        borderRadius: 1.5,
      }}
    >
      {/* Voyage Info */}
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 800, whiteSpace: "nowrap" }}
      >
        {t("voyageNumber")}:{" "}
        {getVoyageNumber(currentVoyage) || "N/A"}
      </Typography>

      {/* Trip Select */}
      <FormControl sx={{ minWidth: 220 }} size="small">
        <InputLabel
          id="trip-select-label"
          sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem" }}
        >
          {t("trip")}
        </InputLabel>
        <Select
          labelId="trip-select-label"
          value={selectedTripId || ""}
          onChange={handleTripChange}
          label="Trip"
          displayEmpty
          sx={{
            color: "white",
            ".MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(255,255,255,0.3)",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "white",
            },
            ".MuiSvgIcon-root": { color: "white" },
            fontSize: "0.85rem",
            fontWeight: 600,
          }}
        >
          {tripsArray.length === 0 ? (
            <MenuItem disabled value="">
              <em>{t("noTrips")}</em>
            </MenuItem>
          ) : (
            tripsArray.map((trip) => (
              <MenuItem key={trip.tripId} value={trip.tripId}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {getTripLocationLabel(trip, t)}
                </Typography>
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>

      {/* Cargo Select */}
      {showCargoSelect && (
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel
            id="cargo-select-label"
            sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem" }}
          >
            {t("cargo")}
          </InputLabel>
          <Select
            labelId="cargo-select-label"
            // Säkerställ att vi inte försöker visa ett index om inga cargos finns
            value={
              selectedTripObject?.cargos?.length > 0 ? selectedCargoIndex : ""
            }
            onChange={handleCargoChange}
            label="Cargo"
            sx={{
              color: "white",
              ".MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255,255,255,0.3)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "white",
              },
              ".MuiSvgIcon-root": { color: "white" },
              fontSize: "0.85rem",
              fontWeight: 600,
            }}
          >
            {selectedTripObject?.cargos?.length > 0 ? (
              selectedTripObject.cargos.map((cargo, index) => (
                <MenuItem key={cargo.cargoId || index} value={index}>
                  <Typography variant="body2">
                    {cargo.cargo || "Unnamed Cargo"} ({cargo.quantity}{" "}
                    {cargo.unit})
                  </Typography>
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled value="">
                <em>{t("noCargo")}</em>
              </MenuItem>
            )}
          </Select>
        </FormControl>
      )}
    </Box>
  );
};

export default VoyageSelectBanner;
